import { zeroErrorMessage } from "@/lib/zero-error";
import { useMutation } from "@tanstack/react-query";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import { generateUUID } from "@/lib/utils";
import type {
  CameraPlan,
  CameraPlanVisualization,
  CameraPlanInsert,
  CameraPlanUpdate,
  CameraPlanColumn,
  CameraPlanColumnInsert,
  CameraPlanColumnUpdate,
  Camera,
  CameraInsert,
  CameraUpdate,
  MapOverlay,
  MapOverlayInsert,
  MapOverlayUpdate,
  OverlayType,
  ZeroDateValue,
} from "@/types/camera-plans";

type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

type CameraPlanWithCameras = CameraPlan & {
  cameras?: Array<
    Camera & {
      cameraFunctionAssignments?: Array<{
        id?: string | null;
        functionAssignmentID?: string | null;
        functionAssignment?: {
          person?: { firstName: string; lastName: string } | null;
        } | null;
      }>;
    }
  >;
};

// === Cameras for a project (cross-plan, used by equipment view) ===

export interface CameraWithPlan {
  id: string;
  number: string;
  description: string | null;
  icon: string;
  equipmentGroupID: string | null;
  cameraPlanID: string;
  cameraPlan?: { id: string; name: string } | null;
  cameraFunctionAssignments?: {
    id: string;
    functionAssignment?: {
      person?: { firstName: string; lastName: string } | null;
    } | null;
  }[];
}

export function useCamerasForProject(projectId: string | undefined) {
  const query = projectId
    ? appQueries.cameraPlans.withCamerasByProject({ projectID: projectId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectId });
  const cameras = ((data ?? []) as CameraPlanWithCameras[]).flatMap((plan) =>
    (plan.cameras ?? []).map(
      (camera) =>
        ({
          ...camera,
          cameraFunctionAssignments: camera.cameraFunctionAssignments?.map(
            (assignment) => ({
              ...assignment,
              id: assignment.id ?? assignment.functionAssignmentID ?? "",
            }),
          ),
          cameraPlan: { id: plan.id, name: plan.name },
        }) satisfies CameraWithPlan,
    ),
  );

  return {
    data: cameras,
    isLoading:
      !!projectId && details.type !== "complete" && cameras.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

// === Camera Plans ===

export function useCameraPlans(projectId: string | undefined) {
  const query = projectId
    ? appQueries.cameraPlans.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectId });

  return {
    data: (data ?? []) as CameraPlan[],
    isLoading: details.type !== "complete" && !data,
  };
}

// Plan rows carry Zero's numeric date, while the mutator takes a YYYY-MM-DD string.
function toPlanDateInput(value: ZeroDateValue | undefined) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value ?? null;
}

export function useCreateCameraPlan() {
  const zero = useZero() as ZeroMutateClient;
  const run = async (plan: CameraPlanInsert) => {
    const id = generateUUID();
    const input = { ...plan, id, eventDate: toPlanDateInput(plan.eventDate) };
    await zero.mutate(appMutators.cameraPlans.createPlan(input)).client;
    const { visualization: _visualization, ...row } = input;
    return { ...row, createdAt: "", updatedAt: "" } satisfies CameraPlan;
  };

  return {
    mutate: (
      plan: CameraPlanInsert,
      options?: { onSuccess?: (data: CameraPlan) => void },
    ) => {
      void run(plan)
        .then((result) => {
          toast.success("Camera plan created");
          options?.onSuccess?.(result);
        })
        .catch((error: Error) => {
          toast.error("Failed to create camera plan: " + error.message);
        });
    },
    mutateAsync: run,
    isPending: false,
  };
}

export function useUpdateCameraPlan() {
  const zero = useZero() as ZeroMutateClient;

  return {
    mutate: (
      {
        id,
        updates,
      }: {
        id: string;
        projectId: string;
        updates: CameraPlanUpdate;
      },
      options?: { onSuccess?: () => void },
    ) => {
      const { eventDate, ...planUpdates } = updates;
      void zero
        .mutate(
          appMutators.cameraPlans.updatePlan({
            id,
            ...planUpdates,
            ...(eventDate !== undefined
              ? { eventDate: toPlanDateInput(eventDate) }
              : {}),
          }),
        )
        .client.then(() => {
          toast.success("Camera plan updated");
          options?.onSuccess?.();
        })
        .catch((error: Error) => {
          toast.error("Failed to update camera plan: " + error.message);
        });
    },
  };
}

export function useDeleteCameraPlan() {
  const zero = useZero() as ZeroMutateClient;

  return {
    mutate: ({ id }: { id: string; projectId: string }) => {
      void zero
        .mutate(appMutators.cameraPlans.deletePlan({ id }))
        .client.then(() => toast.success("Camera plan deleted"))
        .catch((error: Error) => {
          toast.error("Failed to delete camera plan: " + error.message);
        });
    },
  };
}

// === Camera Plan Columns ===

export function useCameraPlanColumns(cameraPlanId: string | undefined) {
  const query = cameraPlanId
    ? appQueries.cameraPlans.columnsByPlan({
        cameraPlanID: cameraPlanId,
      })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!cameraPlanId });

  return {
    data: (data ?? []) as CameraPlanColumn[],
    isLoading: details.type !== "complete" && !data,
  };
}

export function useCreateCameraPlanColumn() {
  const zero = useZero() as ZeroMutateClient;
  const run = async (column: CameraPlanColumnInsert) => {
    const id = generateUUID();
    const input = {
      id,
      cameraPlanID: column.cameraPlanID,
      name: column.name,
      columnType: column.columnType,
      options: column.options,
      sortOrder: column.sortOrder,
    };
    await zero.mutate(appMutators.cameraPlans.createColumn(input)).client;
    return { ...input, createdAt: "" } satisfies CameraPlanColumn;
  };

  return {
    mutate: (
      column: CameraPlanColumnInsert,
      options?: { onSuccess?: (data: CameraPlanColumn) => void },
    ) => {
      void run(column)
        .then((result) => {
          toast.success("Column added");
          options?.onSuccess?.(result);
        })
        .catch((error: Error) => {
          toast.error("Failed to add column: " + error.message);
        });
    },
    mutateAsync: run,
    isPending: false,
  };
}

export function useUpdateCameraPlanColumn() {
  const zero = useZero() as ZeroMutateClient;

  return {
    mutate: (
      {
        id,
        updates,
      }: {
        id: string;
        cameraPlanId: string;
        updates: CameraPlanColumnUpdate;
      },
      options?: { onSuccess?: () => void },
    ) => {
      void zero
        .mutate(
          appMutators.cameraPlans.updateColumn({
            id,
            name: updates.name,
            columnType: updates.columnType,
            options: updates.options,
            sortOrder: updates.sortOrder,
          }),
        )
        .client.then(() => {
          toast.success("Column updated");
          options?.onSuccess?.();
        })
        .catch((error: Error) => {
          toast.error("Failed to update column: " + error.message);
        });
    },
  };
}

export function useDeleteCameraPlanColumn() {
  const zero = useZero() as ZeroMutateClient;

  return {
    mutate: ({ id }: { id: string; cameraPlanId: string }) => {
      void zero
        .mutate(appMutators.cameraPlans.deleteColumn({ id }))
        .client.then(() => toast.success("Column removed"))
        .catch((error: Error) => {
          toast.error("Failed to remove column: " + error.message);
        });
    },
  };
}

// === Cameras ===

export function useCameras(cameraPlanId: string | undefined) {
  const query = cameraPlanId
    ? appQueries.cameraPlans.camerasByPlan({
        cameraPlanID: cameraPlanId,
      })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!cameraPlanId });

  return {
    data: (data ?? []) as Camera[],
    isLoading: details.type !== "complete" && !data,
  };
}

export function useCreateCamera() {
  const zero = useZero() as ZeroMutateClient;
  const run = async (camera: CameraInsert & { optimisticId?: string }) => {
    const id = camera.optimisticId ?? generateUUID();
    const { optimisticId: _optimisticId, ...cameraInput } = camera;
    const input = { ...cameraInput, id };
    await zero.mutate(appMutators.cameraPlans.createCamera(input)).client;
    return { ...input, createdAt: "", updatedAt: "" } satisfies Camera;
  };

  return {
    mutate: (
      camera: CameraInsert & { optimisticId?: string },
      options?: { onSuccess?: (data: Camera) => void },
    ) => {
      void run(camera)
        .then((result) => {
          toast.success("Camera added");
          options?.onSuccess?.(result);
        })
        .catch((error: Error) => {
          toast.error("Failed to add camera: " + error.message);
        });
    },
    mutateAsync: run,
    isPending: false,
  };
}

export function useUpdateCamera() {
  const zero = useZero() as ZeroMutateClient;

  const run = async ({
    id,
    updates,
  }: {
    id: string;
    cameraPlanId: string;
    updates: CameraUpdate;
  }) => {
    await zero.mutate(
      appMutators.cameraPlans.updateCamera({
        id,
        ...updates,
      }),
    ).client;
  };

  return {
    mutate: ({
      id,
      updates,
    }: {
      id: string;
      cameraPlanId: string;
      updates: CameraUpdate;
    }) => {
      void run({ id, cameraPlanId: "", updates }).catch((error: Error) => {
        toast.error("Failed to update camera: " + error.message);
      });
    },
    mutateAsync: run,
  };
}

export function useDeleteCamera() {
  const zero = useZero() as ZeroMutateClient;

  return {
    mutate: ({ id }: { id: string; cameraPlanId: string }) => {
      void zero
        .mutate(appMutators.cameraPlans.deleteCamera({ id }))
        .client.then(() => toast.success("Camera deleted"))
        .catch((error: Error) => {
          toast.error("Failed to delete camera: " + error.message);
        });
    },
  };
}

// === Camera Operator Assignments (junction table) ===

export function useAddCameraOperator() {
  const zero = useZero() as ZeroMutateClient;

  const run = async ({
    cameraId,
    functionAssignmentId,
  }: {
    cameraId: string;
    functionAssignmentId: string;
    cameraPlanId: string;
  }) => {
    const mutation = zero.mutate(
      appMutators.cameraPlans.addOperator({
        id: generateUUID(),
        cameraID: cameraId,
        functionAssignmentID: functionAssignmentId,
      }),
    );
    await mutation.client;
    await reportZeroMutationFailure(
      mutation.server,
      "Camera operator assignment was rejected",
    );
  };

  return {
    mutate: (input: {
      cameraId: string;
      functionAssignmentId: string;
      cameraPlanId: string;
    }) => {
      void run(input).catch((error: Error) => {
        toast.error("Failed to assign operator: " + error.message);
      });
    },
    mutateAsync: run,
  };
}

export function useRemoveCameraOperator() {
  const zero = useZero() as ZeroMutateClient;

  const run = async ({ id }: { id: string; cameraPlanId: string }) => {
    const mutation = zero.mutate(
      appMutators.cameraPlans.removeOperator({ id }),
    );
    await mutation.client;
    await reportZeroMutationFailure(
      mutation.server,
      "Camera operator removal was rejected",
    );
  };

  return {
    mutate: (input: { id: string; cameraPlanId: string }) => {
      void run(input).catch((error: Error) => {
        toast.error("Failed to remove operator: " + error.message);
      });
    },
    mutateAsync: run,
  };
}

// === Image Upload ===

/** Height / width of an image file or URL. */
export function readImageAspect(source: File | string): Promise<number> {
  return new Promise((resolve, reject) => {
    const url =
      typeof source === "string" ? source : URL.createObjectURL(source);
    const img = new Image();
    img.onload = () => {
      if (typeof source !== "string") URL.revokeObjectURL(url);
      if (!img.naturalWidth || !img.naturalHeight) {
        reject(new Error("Image has no size"));
        return;
      }
      resolve(
        Math.max(0.05, Math.min(20, img.naturalHeight / img.naturalWidth)),
      );
    };
    img.onerror = () => {
      if (typeof source !== "string") URL.revokeObjectURL(url);
      reject(new Error("Image could not be read"));
    };
    img.src = url;
  });
}

export function useUploadCameraPlanImage() {
  const zero = useZero() as ZeroMutateClient;

  return useMutation({
    mutationFn: async ({
      visualizationId,
      projectId,
      file,
      updateAspect = true,
    }: {
      visualizationId: string;
      projectId: string;
      file: File;
      /**
       * Store the image's aspect ratio. Off for visualizations from the old
       * image editor: the editor measures the image on first open, when it
       * also converts their placements (adoptImageVisualization).
       */
      updateAspect?: boolean;
    }) => {
      // Upload via proxy endpoint (avoids CORS issues with S3/Swift)
      const res = await fetch(`/api/storage/upload`, {
        method: "POST",
        headers: {
          "Content-Type": file.type,
          "x-file-name": file.name,
        },
        body: file,
      });
      if (!res.ok) throw new Error("Upload failed");
      const { publicUrl } = await res.json();
      const imageAspect = updateAspect
        ? await readImageAspect(file).catch(() => null)
        : null;

      await zero.mutate(
        appMutators.cameraPlans.updateVisualization({
          id: visualizationId,
          imageURL: publicUrl,
          ...(imageAspect ? { imageAspect } : {}),
        }),
      ).client;

      return { imageUrl: publicUrl, projectId };
    },
    onSuccess: () => {
      toast.success("Image uploaded");
    },
    onError: (error) => {
      toast.error("Failed to upload image: " + error.message);
    },
  });
}

// === Visualizations ===

export function useCameraPlanVisualizations(cameraPlanId: string | undefined) {
  const query = cameraPlanId
    ? appQueries.cameraPlans.visualizationsByPlan({
        cameraPlanID: cameraPlanId,
      })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!cameraPlanId });
  return {
    data: (data ?? []) as unknown as CameraPlanVisualization[],
    isLoading: details.type !== "complete" && !data,
  };
}

// === Camera number by function assignment ===

export function useCameraAssignmentMap(projectId: string | undefined) {
  const query = projectId
    ? appQueries.cameraPlans.withCamerasByProject({ projectID: projectId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectId });
  const map: Record<string, string> = {};
  for (const plan of (data ?? []) as CameraPlanWithCameras[]) {
    for (const camera of plan.cameras ?? []) {
      for (const link of camera.cameraFunctionAssignments ?? []) {
        if (link.functionAssignmentID && camera.number) {
          map[link.functionAssignmentID] = camera.number;
        }
      }
    }
  }

  return {
    data: map,
    isLoading: !!projectId && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}
